import { CheckCircle } from "lucide-react";
import { HorizLineDivider } from "../Dividers";
import React from "react";
import { getEventDataHook, readCartSuccess } from "../../hooks";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../layout";
import UserTicketCard from "./UserTicketCard";
import LoadingScreen from "../loadingscreen";

export default function RegistrationSuccess({
  userEmail,
  userFirstName,
  additionalNotes,
  updateSubmission,
  submissionID = "gC2Wc8yo0g",
  userID = "ZUAnAU8ey6",
  cardInfo,
  ...props
}) {
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const email = decodeURIComponent(String(queryParams.get("email")));

  const [isLoading, setIsLoading] = React.useState(true);

  const [eventData, setEventData] = React.useState(null);
  const [orderData, setOrderData] = React.useState([]);

  const { eventid, orderid } = useParams();

  React.useEffect(() => {
    async function loadData() {
      // get the event data and order data from eventid and orderid
      let [responseEventData, responseOrderData] = await Promise.all([
        getEventDataHook(eventid),
        readCartSuccess(orderid, email),
      ]);

      // error handling
      if (responseEventData.status !== "200") {
        navigate("/error/" + responseEventData.status);
        return null;
      }

      if (responseOrderData.status !== "200") {
        console.error(
          "responseOrderData navigating to error",
          responseOrderData
        );
        navigate("/error/" + responseOrderData.status);
        return null;
      }

      let orderItems = responseOrderData.orders?.[0]?.items;

      console.log("orderItems", orderItems);

      // set the event data and order data
      setEventData(responseEventData.events);
      setOrderData(orderItems);

      setIsLoading(false);
    }

    window.scrollTo(0, 0);
    loadData();
  }, [eventid, orderid, navigate, email]);

  return (
    <Layout>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <>
          <div className="flex flex-col w-full justify-start items-center px-4 mt-6">
            <div className="flex flex-col w-full max-w-screen-lg">
              <h1 className="text-2xl text-black font-bold tracking-normal">
                {eventData?.name !== undefined
                  ? eventData.name
                  : "Unknown Event"}
              </h1>
            </div>
          </div>
          <div className="flex flex-col grow w-full h-full justify-start sm:justify-center items-center mb-60 px-4 py-8">
            <div className="flex flex-col w-full h-full max-w-screen-lg items-center justify-center overflow-hidden gap-4">
              <div className="flex flex-wrap gap-2 justify-center items-center w-full">
                <CheckCircle
                  className="w-12 h-12 sm:w-20 sm:h-20 text-green-500"
                  strokeWidth={2.5}
                />
                <div className="flex flex-col gap-0">
                  <h1 className="text-lg sm:text-2xl text-green-500 font-bold tracking-tight text-center">
                    {updateSubmission
                      ? "Registration Updated!"
                      : "Registration Successful!"}
                  </h1>
                  <h2 className="text-sm sm:text-lg text-green-500 font-normal tracking-tight text-center">
                    Please save this page for your records
                  </h2>
                </div>
              </div>
              <section className="flex flex-col space-y-4 rounded-xl mt-5 w-full">
                <div>
                  <div className="flex flex-col p-4 md:p-4 justify-between bg-green-100 border-2 border-green-200 rounded-xl gap-2">
                    <h2 className="text-md font-bold text-green-800">
                      Thank you for registering for{" "}
                      {eventData?.name ? eventData.name : "Unknown Event"}!
                    </h2>
                    {updateSubmission ? (
                      <h2 className="text-sm font-normal text-green-800">
                        Your submission has been updated. You may use your
                        original eTicket to access the event.
                      </h2>
                    ) : (
                      <h2 className="text-sm font-normal text-green-800">
                        Your eTicket is down below and your receipt has been
                        emailed to {email ? email : "Unknown Email"}
                      </h2>
                    )}
                    <button
                      className="w-fit px-2 py-1 rounded-lg hover:bg-green-300 bg-green-300/70 transition-colors duration-200 ease-in-out"
                      onClick={() => {
                        window.location.href = "#ticket";
                      }}
                    >
                      <p className="text-xs text-green-600 font-semibold">
                        View Ticket
                      </p>
                    </button>
                  </div>
                </div>
              </section>
              <section className="flex flex-col space-y-4 rounded-xl w-full">
                <div>
                  <div className="flex flex-col p-4 md:p-4 space-y-1 justify-between bg-slate-50 border-2 border-slate-100 rounded-xl">
                    <h2 className="text-md font-bold text-slate-800">
                      Additional notes from the event organizer
                    </h2>
                    <h2 className="text-sm font-normal text-slate-800 p-0 mt-0">
                      {additionalNotes
                        ? additionalNotes
                        : "No additional notes"}
                    </h2>
                  </div>
                </div>
              </section>
            </div>
            <HorizLineDivider className="my-8" />
            <div className="flex flex-col w-full max-w-screen-lg">
              <div className="flex flex-col w-full p-4 bg-blue-100 border-2 border-blue-200 rounded-xl gap-2">
                <h1 className="text-lg text-blue-800 font-bold tracking-normal">
                  This is your eTicket
                </h1>
                <p className="text-sm text-blue-800 font-normal">
                  This QR code and eTicket serves as your admission into the
                  event. Present and scan this QR code at check-in. Please save
                  this page for your records
                </p>
              </div>
            </div>
            <div className="flex flex-col w-full max-w-screen-lg pt-4 gap-4">
              {orderData &&
                orderData.map((item) => {
                  return (
                    <UserTicketCard
                      key={item.submissionInfo.submissionID}
                      firstName={item.submissionInfo.firstName}
                      lastName={item.submissionInfo.lastName}
                      purchaseDate={item.submissionInfo.purchaseDate}
                      activationCode={item.submissionInfo.activation}
                      notes={item.formInfo.notes}
                      eventData={eventData}
                      seatRow={item.submissionInfo.r}
                      seatCol={item.submissionInfo.c}
                    />
                  );
                })}
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
